import React from "react";
import { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { Localizer } from "@/main/react/Types";
import { ShareEntityType, ShareMomentKind } from "@/main/util/PlatformIdentifierUtil";
import { hasShownShareMoment, recordShareMomentShown } from "@/main/share/shareMomentLedger";
import ShareMomentDialog from "@/main/react/share/ShareMomentDialog";
import { trackShareMomentShown } from "@/main/analytics/analyticsEvents";

export interface ShareMomentInput {
    kind: ShareMomentKind;
    entityType: ShareEntityType;
    entityId: string;
    /** Absolute, TOKEN-FREE — the chokepoint appends `?sv_s`. */
    url: string;
    title: string;
    authorDisplayName?: string;
    previewImageUrl?: string;
    localizer: Localizer;
    /**
     * Called once when the member closes the dialog. Lets a caller queue the NEXT ask behind this one
     * instead of stacking on it (the Play review moment, review-prompt design D7).
     */
    onClosed?: () => void;
}

/**
 * Opens a share moment, or does nothing.
 *
 * <p>Returns `true` only when a dialog was actually opened, so callers can tell "shown" from "suppressed"
 * without re-deriving the rules.
 *
 * <p>Two suppressions, both deliberate:
 *
 * <ul>
 *   <li><strong>Never stack over an open dialog.</strong> There is no modal-stack registry in this app, so
 *       the only available check is a DOM probe for an existing `[role="dialog"]` — the same probe
 *       `openGroupWelcomeDialog` uses, copied rather than reinvented. Losing the moment is strictly better
 *       than stacking two modals on a first-session user who is already looking at a welcome overlay.</li>
 *   <li><strong>Once per (kind, entityId), forever.</strong> Recorded the instant the dialog opens, not on
 *       dismissal, so a reload cannot re-show it.</li>
 * </ul>
 */
export function openShareMoment(input: ShareMomentInput): boolean {
    if (typeof document === "undefined") {
        return false;
    }
    if (!input.entityId || !input.url) {
        return false;
    }
    if (hasShownShareMoment(input.kind, input.entityId)) {
        return false;
    }
    // No modal-stack registry exists; this DOM probe is the whole mechanism.
    if (document.querySelector('[role="dialog"]')) {
        return false;
    }

    recordShareMomentShown(input.kind, input.entityId);
    // The denominator for shown -> shared. Emitted here rather than in the dialog so it fires exactly
    // once per actual open, after every suppression has already returned.
    trackShareMomentShown("discussion", `share_moment_${input.kind}`);

    let closedNotified = false;
    const close = openSimpleDialog(
        ({ open, onOpenChange }) => (
            <ShareMomentDialog
                kind={input.kind}
                entityType={input.entityType}
                entityId={input.entityId}
                url={input.url}
                title={input.title}
                authorDisplayName={input.authorDisplayName}
                previewImageUrl={input.previewImageUrl}
                localizer={input.localizer}
                open={open}
                onOpenChange={(next) => {
                    onOpenChange(next);
                    if (!next && !closedNotified) {
                        closedNotified = true;
                        input.onClosed?.();
                    }
                }}
            />
        ),
        { "data-share-moment": input.kind },
    );

    void close;
    return true;
}

export default openShareMoment;
